import type { PaymentToSchedule } from 'src/core/modules/invoices/models/paymentToSchedule';
import type {
  InvoiceInconsistencyItem,
  InconsistencyMatchingResult,
} from 'src/core/modules/verification-of-payee/models';
import { isVerificationOfPayeeResultInvalid } from 'src/core/modules/verification-of-payee/utils';

export type GroupedPaymentsByCounterparty = {
  counterpartyId: string;
  iban: string;
  accountHolderName: string;
  paymentIds: string[];
  invoiceCount: number;
};

export const groupPaymentsByCounterparty = (
  payments: PaymentToSchedule[],
): GroupedPaymentsByCounterparty[] => {
  const groupMap = new Map<string, GroupedPaymentsByCounterparty>();

  for (const payment of payments) {
    const { id: counterpartyId, bankAddress } = payment.bill.counterparty;
    const iban = bankAddress?.iban;
    const accountHolderName = bankAddress?.accountHolderName;

    if (!iban || !accountHolderName) {
      continue;
    }

    const existing = groupMap.get(counterpartyId);

    if (existing) {
      existing.paymentIds.push(payment.id);
      existing.invoiceCount += 1;
    } else {
      groupMap.set(counterpartyId, {
        counterpartyId,
        iban,
        accountHolderName,
        paymentIds: [payment.id],
        invoiceCount: 1,
      });
    }
  }

  return Array.from(groupMap.values());
};

export const filterPaymentsWithInvalidInitialVerificationOfPayee = (
  payments: PaymentToSchedule[],
) =>
  payments.filter((payment) => {
    const vop = payment.verificationOfPayee;
    return (
      vop &&
      vop.initialResult &&
      isVerificationOfPayeeResultInvalid(vop.initialResult.matchingResult)
    );
  });

export const filterPaymentsWithInvalidVerificationOfPayee = (
  payments: PaymentToSchedule[],
) =>
  payments.filter((payment) => {
    const vop = payment.verificationOfPayee;
    const latestResult = vop?.updatedResult ?? vop?.initialResult;

    return (
      vop &&
      latestResult &&
      isVerificationOfPayeeResultInvalid(latestResult.matchingResult)
    );
  });

export const mapGroupedPaymentsToInconsistencyItems = (
  groupedPayments: GroupedPaymentsByCounterparty[],
  getMatchingResult: (counterpartyId: string) => InconsistencyMatchingResult,
): InvoiceInconsistencyItem[] =>
  groupedPayments.map((group) => ({
    id: group.counterpartyId,
    accountHolderName: group.accountHolderName,
    invoiceCount: group.invoiceCount,
    matchingResult: getMatchingResult(group.counterpartyId),
  }));
